// Copyright (c) Mysten Labs, Inc. — DGC Stake Modal
// SPDX-License-Identifier: Apache-2.0

import { useSuiClientQuery } from '@mysten/dapp-kit';
import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { useCpcValidatorsApy } from '~/hooks/useCpcValidatorsApy';
import { formatDailyYieldPercent, formatEpochCountdown } from '~/utils/explorerFormat';
import { sortValidatorsByStake } from '~/utils/sortValidatorsByStake';
import { CPC_SYMBOL } from '~/utils/cpcCoin';

interface StakeModalProps {
	isOpen: boolean;
	onClose: () => void;
	/** Total CPC balance (display). */
	balance?: string;
	/** Max stakeable after gas reserve (MAX button). */
	stakeableBalance?: string;
	symbol?: string;
	onConfirm?: (amount: string, validators: string[]) => void;
}

export function StakeModal({
	isOpen,
	onClose,
	balance = '0',
	stakeableBalance,
	symbol = CPC_SYMBOL,
	onConfirm,
}: StakeModalProps) {
	const [amount, setAmount] = useState('');
	const { data: systemState, isLoading } = useSuiClientQuery('getLatestSuiSystemState');
	const validators = systemState?.activeValidators ?? [];
	const { avgApy, isUsingEstimates } = useCpcValidatorsApy(validators);

	if (!isOpen) return null;

	const epochDuration =
		systemState?.epochDurationMs != null
			? formatEpochCountdown(Number(systemState.epochDurationMs))
			: '—';

	const maxStake = stakeableBalance ?? balance;

	const handleMaxClick = () => {
		setAmount(maxStake.replace(/,/g, ''));
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		const maxNum = parseFloat(maxStake.replace(/,/g, ''));
		const balNum = parseFloat(balance.replace(/,/g, ''));
		if (!Number.isFinite(maxNum) || maxNum <= 0) {
			toast.error(`当前 ${symbol} 可质押余额为 0（请预留 Gas）`);
			return;
		}
		if (!amount || parseFloat(amount) <= 0) {
			toast.error('请输入有效的质押数量');
			return;
		}
		if (parseFloat(amount) > maxNum) {
			toast.error(
				maxStake !== balance
					? `超过可质押余额（需预留少量 ${symbol} 支付 Gas）`
					: '超过可用余额',
			);
			return;
		}
		if (!systemState) {
			toast.error('正在加载网络节点，请稍后再试');
			return;
		}

		const addresses = sortValidatorsByStake(systemState.activeValidators).map((v) => v.suiAddress);
		onConfirm?.(amount, addresses);
		setAmount('');
		onClose();
	};

	return (
		<>
			<div
				onClick={onClose}
				className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm"
			/>
			<div className="fixed top-1/2 left-1/2 z-[101] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-[#1E293B] bg-[#1E222F] p-6 text-white shadow-2xl">
				<div className="flex items-center justify-between border-b border-[#1E293B]/60 pb-4">
					<h3 className="text-lg font-bold">质押 {symbol}</h3>
					<button
						type="button"
						onClick={onClose}
						className="rounded-lg p-1 text-[#AEB9D0] hover:bg-[#1E293B]"
					>
						<XMarkIcon className="h-5 w-5" />
					</button>
				</div>

				<form onSubmit={handleSubmit} className="mt-5 space-y-5">
					<div className="space-y-2">
						<div className="flex justify-between text-xs text-[#AEB9D0]">
							<span>质押数量</span>
							<span>
								可质押{' '}
								<strong className="text-white">
									{maxStake} {symbol}
								</strong>
								{maxStake !== balance ? (
									<span className="text-[#64748b]"> / 总 {balance}</span>
								) : null}
							</span>
						</div>
						<div className="relative flex items-center rounded-xl border border-[#1E293B] bg-[#0F172A] px-4 py-3 focus-within:border-[#38BDF8]">
							<input
								type="number"
								step="any"
								min="0"
								placeholder="0.0"
								value={amount}
								onChange={(e) => setAmount(e.target.value)}
								className="w-full bg-transparent pr-16 font-semibold text-white outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
							/>
							<div className="absolute right-4 flex items-center gap-2">
								<button
									type="button"
									onClick={handleMaxClick}
									className="rounded bg-[#38BDF8]/10 px-2 py-0.5 text-xs font-bold text-[#38BDF8]"
								>
									MAX
								</button>
								<span className="text-sm font-bold text-gray-300">{symbol}</span>
							</div>
						</div>
					</div>

						<div className="rounded-xl bg-[#0F172A]/50 p-3.5 border border-[#1E293B]/40 text-xs text-[#AEB9D0] space-y-1.5">
							{maxStake !== balance ? (
								<p className="text-[#64748b]">Gas 从 {symbol} 扣除；若余额全在地址余额中，MAX 已预留少量 Gas。</p>
							) : null}
						<div className="flex justify-between">
							<span>当前 Epoch</span>
							<span className="text-white">{systemState?.epoch ?? '—'}</span>
						</div>
						<div className="flex justify-between">
							<span>Epoch 周期（约）</span>
							<span className="text-white">{epochDuration}</span>
						</div>
						<div className="flex justify-between">
							<span>网络平均日化</span>
							<span className="text-emerald-400 font-medium">
								{formatDailyYieldPercent(avgApy, { estimated: isUsingEstimates })}
							</span>
						</div>
					</div>

					<button
						type="submit"
						disabled={isLoading}
						className="w-full h-12 rounded-xl bg-[#38BDF8] text-sm font-bold text-white hover:bg-[#38BDF8]/90 disabled:opacity-50"
					>
						{isLoading ? '加载节点中…' : '选择验证者'}
					</button>
				</form>
			</div>
		</>
	);
}
